"use client";

import { Button } from "@/components/ui/button";
import { Search, MapPin } from "lucide-react";
import { useState, useCallback } from "react";
import { AnimatePresence } from "motion/react";
import { useRouter } from "next/navigation";
import { useClientMotion } from '@/lib/hooks/use-reduced-motion-ssr';

interface SearchSuggestion {
  id: string;
  address: string;
  city: string;
  state: string;
  country: string;
  type: 'address' | 'city' | 'area';
}

interface SearchBarProps {
  placeholder?: string;
  buttonText?: string;
  onSearch?: (query: string) => void;
  className?: string;
}

const SearchBar = ({
  placeholder = "Enter Address, City, or Zip",
  buttonText = "Search Reviews",
  onSearch,
  className = "",
}: SearchBarProps) => {
  const [query, setQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  
  // ✅ 2025 STANDARD: Conditional component rendering
  const { MotionDiv, motionProps } = useClientMotion();

  const fetchSuggestions = useCallback(async (searchQuery: string) => {
    if (!searchQuery || searchQuery.length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch(`/api/search?q=${encodeURIComponent(searchQuery)}&autocomplete=true`);
      
      if (!response.ok) {
        console.error('Search API error:', response.status, response.statusText);
        setSuggestions([]);
        return;
      }
      
      const data = await response.json();
      
      if (data.status === 'success' && Array.isArray(data.suggestions)) {
        const searchSuggestions: SearchSuggestion[] = data.suggestions.map((suggestion: string, index: number) => ({
          id: `suggestion-${index}`,
          address: suggestion,
          city: '',
          state: '',
          country: '',
          type: 'address' as const
        }));
        
        setSuggestions(searchSuggestions);
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      console.error('Failed to fetch search suggestions:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    fetchSuggestions(value);
  };

  const handleSearch = () => {
    if (query.trim()) {
      // Redirect to browse page with search query
      router.push(`/browse?q=${encodeURIComponent(query.trim())}`);
      if (onSearch) {
        onSearch(query.trim());
      }
    }
  };

  const handleSuggestionSelect = (suggestion: SearchSuggestion) => {
    // Redirect to browse page with selected suggestion
    router.push(`/browse?q=${encodeURIComponent(suggestion.address)}`);
    setSuggestions([]);
    if (onSearch) {
      onSearch(suggestion.address);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
    if (e.key === "Escape") {
      setSuggestions([]);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (query && query.length >= 2) {
      fetchSuggestions(query);
    }
  };

  const handleBlur = () => {
    // Delay hiding suggestions to allow clicks
    setTimeout(() => setIsFocused(false), 150);
  };

  return (
    <MotionDiv 
      {...motionProps({
        initial: { scale: 0.95, opacity: 0 },
        animate: { scale: 1, opacity: 1 },
        transition: { duration: 0.3 }
      })}
      className={`relative ${className}`}
    >
      <div 
        className={`
          flex items-center w-full p-1 rounded-2xl border-2 transition-all duration-300 transform-gpu bg-white/90 backdrop-blur-sm shadow-lg
          ${isFocused 
            ? 'border-blue-500 shadow-xl scale-[1.02]' 
            : 'border-white/50 hover:border-blue-300 hover:shadow-xl'
          }
        `}
      >
        <div className="flex-1 flex items-center px-4">
          <MapPin className="h-5 w-5 text-gray-400 mr-3" />
          <input
            type="text"
            placeholder={placeholder}
            className="w-full py-2.5 text-gray-800 text-lg bg-transparent focus:outline-none"
            style={{ 
              border: 'none', 
              outline: 'none',
              boxShadow: 'none',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              appearance: 'none'
            }}
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </div>
        <Button
          onClick={handleSearch}
          className="mr-1 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors shadow-md"
          disabled={isLoading}
        >
          <Search className="h-4 w-4 mr-2" />
          <span className="hidden sm:inline">{buttonText}</span>
          <span className="sm:hidden">Search</span>
        </Button>
      </div>

      <AnimatePresence>
        {suggestions.length > 0 && (
          <MotionDiv
            {...motionProps({
              initial: { opacity: 0, y: -10 },
              animate: { opacity: 1, y: 0 },
              exit: { opacity: 0, y: -10 },
              transition: { duration: 0.2 }
            })}
            className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg overflow-hidden z-50"
          >
            <div className="max-h-60 overflow-y-auto">
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion.id}
                  onClick={() => handleSuggestionSelect(suggestion)}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
                >
                  <div className="flex items-center">
                    <MapPin className="h-4 w-4 text-gray-400 mr-3 flex-shrink-0" />
                    <div>
                      <div className="text-gray-800 font-medium">{suggestion.address}</div>
                      {suggestion.city && (
                        <div className="text-sm text-gray-500">{suggestion.city}</div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </MotionDiv>
        )}
      </AnimatePresence>
    </MotionDiv>
  );
};

export default SearchBar;
